import { Close } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { Combine, Copy, Rotate3d, Trash2 } from 'lucide-react';
import {
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import DisabledHint from '~/components/Tooltips/DisabledHint';
import {
    CardHeaderAction,
    DetailsCard,
    DetailsCardHeader,
} from '~/components/DetailsCard';
import {
    RoiReadModel,
    StructureSetReadModel,
} from '~/domain/readmodels/StructureSetReadModel';
import { usePrimaryImageCompletenessGate } from '~/hooks/image/useImageStreamProgress';
import { useStructureSetCommands } from '~/hooks/domain/useStructureSetCommands';
import { BulkUnionOutcome } from '~/hooks/structure-set/useBulkUnion';
import { useCopyStructureSetFlow } from '~/hooks/structure-set/useCopyStructureSetFlow';
import {
    autoShownRoiNumberState,
    selectedROIState,
} from '~/state/atoms/structureSet/roiAtoms';
import { roiPreview3DOpenState } from '~/state/atoms/viewerAtoms';
import { isTextEntryElement } from '~/utils/textEntry';
import BulkDeletionConfirmationDialog from './BulkDeletionConfirmationDialog';
import BulkUnionDialog from './BulkUnionDialog';
import CopyStructureSetDialog from './CopyStructureSetDialog/CopyStructureSetDialog';
import { sortRois } from './roiSort';
import StructureChip from './StructureChip';

type BulkActionsPanelProps = {
    structureSet: StructureSetReadModel;
    selectedRoiNumbers: ReadonlySet<number>;
    hiddenSelectedRoiNumbers: ReadonlySet<number>;
    onClearSelection: () => void;
    onRemoveFromSelection: (roiNumber: number) => void;
    /**
     * Renders the panel for a view that must not author the displayed set (the
     * Dose and Review steps). Disables Delete and disarms its keyboard
     * shortcut. Copy stays enabled: it never touches the displayed set — it
     * writes a SEPARATE structure set through its own dialog — and the same
     * dialog is already reachable from the structure-set header in these very
     * views, so blocking it here would remove no hazard.
     */
    readOnly?: boolean;
};

type BulkActionButtonProps = {
    icon: ReactNode;
    label: ReactNode;
    color: 'primary' | 'error';
    disabled?: boolean;
    onClick: () => void;
    testId: string;
    /** Filled while the action it toggles is on. */
    active?: boolean;
};

function BulkActionButton({
    icon,
    label,
    color,
    disabled = false,
    onClick,
    testId,
    active = false,
}: BulkActionButtonProps) {
    return (
        <Button
            fullWidth
            variant={active ? 'contained' : 'outlined'}
            color={color}
            size="small"
            disabled={disabled}
            onClick={onClick}
            data-testid={testId}
            aria-pressed={active}
            sx={{ fontSize: 12, py: 0.5, textTransform: 'none' }}
        >
            <Box
                component="span"
                sx={{ position: 'absolute', left: 8, display: 'inline-flex' }}
            >
                {icon}
            </Box>
            {label}
        </Button>
    );
}

export default function BulkActionsPanel({
    structureSet,
    selectedRoiNumbers,
    hiddenSelectedRoiNumbers,
    onClearSelection,
    onRemoveFromSelection,
    readOnly = false,
}: BulkActionsPanelProps) {
    const intl = useIntl();
    const { removeRois } = useStructureSetCommands();
    const setSelectedROI = useSetRecoilState(selectedROIState);
    const setAutoShown = useSetRecoilState(autoShownRoiNumberState);
    const [isPreviewOpen, setPreviewOpen] = useRecoilState(
        roiPreview3DOpenState
    );
    const selectedROINumber = useRecoilValue(selectedROIState);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [copyOpen, setCopyOpen] = useState(false);
    const [unionOpen, setUnionOpen] = useState(false);

    const handleCloseCopyDialog = useCallback(() => {
        setCopyOpen(false);
    }, []);

    const onCopySuccess = useCallback(() => {
        setCopyOpen(false);
        onClearSelection();
    }, [onClearSelection]);

    const { patient, sourceImage, handleConfirmCopy } = useCopyStructureSetFlow(
        structureSet,
        onCopySuccess
    );

    const selectedRois = useMemo(
        () =>
            structureSet.roiArray
                .filter((roi) => selectedRoiNumbers.has(roi.roiNumber))
                .sort(sortRois),
        [structureSet.roiArray, selectedRoiNumbers]
    );

    // Approved ROIs are excluded from the batch (long-standing behaviour); a
    // locked ROI instead disables bulk deletion outright — SR-WOR-H-42-1
    // rejects a batch containing one in full. The blocker already ranks
    // approval above the lock, so an ROI that is both excludes, not aborts.
    const {
        deletableRois,
        nonDeletableRois,
        deletableRoiNumbers,
        lockedRoiNumbers,
        approvedCount,
    } = useMemo(() => {
        const deletable: RoiReadModel[] = [];
        const nonDeletable: RoiReadModel[] = [];
        const deletableNumbers = new Set<number>();
        const lockedNumbers = new Set<number>();
        let approved = 0;
        for (const roi of selectedRois) {
            switch (roi.contourEditBlocker) {
                case 'approval':
                    nonDeletable.push(roi);
                    approved += 1;
                    break;
                case 'lock':
                    nonDeletable.push(roi);
                    lockedNumbers.add(roi.roiNumber);
                    break;
                default:
                    deletable.push(roi);
                    deletableNumbers.add(roi.roiNumber);
            }
        }
        return {
            deletableRois: deletable,
            nonDeletableRois: nonDeletable,
            deletableRoiNumbers: deletableNumbers,
            lockedRoiNumbers: lockedNumbers,
            approvedCount: approved,
        };
    }, [selectedRois]);

    const handleConfirmDelete = useCallback(() => {
        const deletableNumbers = [...deletableRoiNumbers].sort((a, b) => a - b);
        const deleted = removeRois(structureSet.id, deletableNumbers);
        // Close the dialog either way — a rejection surfaces as a snackbar, and
        // a modal left open over it reads as an unresponsive confirm.
        setConfirmOpen(false);
        if (!deleted) {
            // removeRois is all-or-nothing: nothing was deleted, so the
            // structures are all still there. Keep the selection and the active
            // ROI so the user can act on the snackbar and retry, instead of
            // re-picking every structure by hand.
            return;
        }
        if (deletableNumbers.includes(selectedROINumber)) {
            setSelectedROI(-1);
            setAutoShown(null);
        }
        onClearSelection();
    }, [
        deletableRoiNumbers,
        selectedROINumber,
        setSelectedROI,
        setAutoShown,
        onClearSelection,
        removeRois,
        structureSet.id,
    ]);

    const deleteDisabled =
        readOnly ||
        deletableRois.length === 0 ||
        structureSet.isApproved ||
        lockedRoiNumbers.size > 0;

    // Name the condition the user can act on: any locked member is the
    // operative blocker — unlocking or deselecting it enables the button.
    const deleteDisabledReasonId = readOnly
        ? 'structure.bulk.deleteDisabled.readOnly'
        : structureSet.isApproved
          ? 'structure.bulk.deleteDisabled.structureSetApproved'
          : lockedRoiNumbers.size > 0
            ? 'structure.bulk.deleteDisabled.locked'
            : 'structure.bulk.deleteDisabled.approved';

    // Union authors the DISPLAYED structure set, so — unlike Copy, which
    // writes a separate one — it follows Delete and is unavailable in the
    // read-only views and on an approved set. It also needs two structures
    // to combine: the panel is on screen from the moment multi-select is
    // armed, so it is reachable with nothing picked yet, and a dialog that
    // opens only to refuse is worse than a button that says why.
    const tooFewForUnion = selectedRois.length < 2;
    // A union is geometry the user accepts by looking at it against the
    // anatomy underneath. On a volume that is not all there, the slices that
    // would show whether the result is right may not have arrived, so the
    // check that makes accepting it a decision cannot be made — the same
    // reason the boolean and margin operations gate (SR-WOR-H-37-3).
    const imageGate = usePrimaryImageCompletenessGate();
    const unionDisabled =
        readOnly ||
        structureSet.isApproved ||
        tooFewForUnion ||
        imageGate.isBlocked;

    const handleUnionSuccess = useCallback(
        (outcome: BulkUnionOutcome) => {
            setUnionOpen(false);
            // Defensive: the newly created union is normally the active ROI
            // by the time the sources go, but never leave the active ROI
            // pointing at a number that was just freed for reuse.
            if (outcome.deletedRoiNumbers.includes(selectedROINumber)) {
                setSelectedROI(-1);
                setAutoShown(null);
            }
            if (outcome.deleteOutcome === 'refused') {
                // The opt-in deletion was rejected (removeRois is
                // all-or-nothing): every source is still present. Keep the
                // selection — the same gating handleConfirmDelete applies —
                // so the user can act on the snackbar and retry the delete
                // instead of re-picking every structure by hand.
                return;
            }
            onClearSelection();
        },
        [selectedROINumber, setSelectedROI, setAutoShown, onClearSelection]
    );

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (
                confirmOpen ||
                copyOpen ||
                unionOpen ||
                isTextEntryElement(event.target)
            ) {
                return;
            }
            // An Escape something else already handled, such as a tool
            // cancelling its action in progress, keeps the selection.
            if (event.key === 'Escape' && !event.defaultPrevented) {
                onClearSelection();
                // readOnly is re-checked here and not only folded into
                // deleteDisabled: this shortcut opens a DESTRUCTIVE dialog with
                // no pointer affordance to hint it is unavailable, so it states
                // its own precondition rather than inheriting one.
            } else if (event.key === 'Delete' && !readOnly && !deleteDisabled) {
                setConfirmOpen(true);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [
        confirmOpen,
        copyOpen,
        unionOpen,
        onClearSelection,
        deleteDisabled,
        readOnly,
    ]);
    const copyDisabled = !patient || !sourceImage;

    const unionButton = (
        <BulkActionButton
            icon={<Combine size={14} />}
            label={<FormattedMessage id="structure.bulk.unionButton" />}
            color="primary"
            disabled={unionDisabled}
            onClick={() => setUnionOpen(true)}
            testId="bulk-union-button"
        />
    );

    const deleteButton = (
        <BulkActionButton
            icon={<Trash2 size={14} />}
            label={<FormattedMessage id="structure.bulk.deleteButton" />}
            color="error"
            disabled={deleteDisabled}
            onClick={() => setConfirmOpen(true)}
            testId="bulk-delete-button"
        />
    );

    return (
        <>
            <DetailsCard data-testid="bulk-actions-card">
                <DetailsCardHeader>
                    <Typography
                        aria-live="polite"
                        fontSize={14}
                        fontWeight={500}
                        flex={1}
                        minWidth={0}
                        noWrap
                    >
                        <FormattedMessage
                            id="structure.bulk.selectedCount"
                            values={{
                                count: selectedRois.length,
                                total: structureSet.roiArray.length,
                            }}
                        />
                    </Typography>
                    <CardHeaderAction
                        Icon={Close}
                        label={intl.formatMessage({
                            id: 'structure.bulk.clearSelection',
                        })}
                        onClick={onClearSelection}
                        data-testid="close-bottom-panel"
                    />
                </DetailsCardHeader>
                <Box
                    display="flex"
                    flexWrap="wrap"
                    gap={0.5}
                    sx={{ maxHeight: 88, overflowY: 'auto' }}
                    data-testid="bulk-chip-list"
                >
                    {selectedRois.map((roi) => (
                        <StructureChip
                            key={roi.roiNumber}
                            roi={roi}
                            testId={`bulk-chip-${roi.roiNumber}`}
                            isHidden={hiddenSelectedRoiNumbers.has(
                                roi.roiNumber
                            )}
                            undeletableReason={
                                deletableRoiNumbers.has(roi.roiNumber)
                                    ? undefined
                                    : lockedRoiNumbers.has(roi.roiNumber)
                                      ? 'lock'
                                      : 'approval'
                            }
                            onRemove={onRemoveFromSelection}
                        />
                    ))}
                </Box>
                {hiddenSelectedRoiNumbers.size > 0 && (
                    <Typography
                        variant="caption"
                        color="warning.main"
                        data-testid="bulk-hidden-caption"
                    >
                        <FormattedMessage
                            id="structure.bulk.hiddenByFilters"
                            values={{ count: hiddenSelectedRoiNumbers.size }}
                        />
                    </Typography>
                )}
                {approvedCount > 0 && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        data-testid="bulk-approved-caption"
                    >
                        <FormattedMessage
                            id="structure.bulk.approvedCount"
                            values={{ count: approvedCount }}
                        />
                    </Typography>
                )}
                {lockedRoiNumbers.size > 0 && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        data-testid="bulk-locked-caption"
                    >
                        <FormattedMessage
                            id="structure.bulk.lockedCount"
                            values={{ count: lockedRoiNumbers.size }}
                        />
                    </Typography>
                )}
                <Box display="flex" flexDirection="column" gap={0.75} mt={0.5}>
                    <BulkActionButton
                        icon={<Rotate3d size={14} />}
                        label={
                            <FormattedMessage id="structure.bulk.preview3D" />
                        }
                        color="primary"
                        active={isPreviewOpen}
                        onClick={() => setPreviewOpen((open) => !open)}
                        testId="bulk-preview-3d-button"
                    />
                    <BulkActionButton
                        icon={<Copy size={14} />}
                        label={
                            <FormattedMessage id="structure.bulk.copyButton" />
                        }
                        color="primary"
                        disabled={copyDisabled}
                        onClick={() => setCopyOpen(true)}
                        testId="bulk-copy-button"
                    />
                    {unionDisabled ? (
                        <DisabledHint
                            placement="right"
                            title={
                                imageGate.reason ??
                                intl.formatMessage({
                                    id: readOnly
                                        ? 'structure.bulk.union.disabled.readOnly'
                                        : structureSet.isApproved
                                          ? 'structure.bulk.union.disabled.structureSetApproved'
                                          : 'structure.bulk.union.disabled.tooFewSources',
                                })
                            }
                            spanStyle={{ display: 'flex', width: '100%' }}
                        >
                            {unionButton}
                        </DisabledHint>
                    ) : (
                        unionButton
                    )}
                    {deleteDisabled ? (
                        <DisabledHint
                            placement="right"
                            title={intl.formatMessage({
                                id: deleteDisabledReasonId,
                            })}
                            spanStyle={{ display: 'flex', width: '100%' }}
                        >
                            {deleteButton}
                        </DisabledHint>
                    ) : (
                        deleteButton
                    )}
                </Box>
            </DetailsCard>
            <BulkDeletionConfirmationDialog
                open={confirmOpen}
                deletableRois={deletableRois}
                nonDeletableRois={nonDeletableRois}
                onConfirm={handleConfirmDelete}
                onCancel={() => setConfirmOpen(false)}
            />
            {unionOpen && (
                <BulkUnionDialog
                    open={unionOpen}
                    structureSet={structureSet}
                    selectedRoiNumbers={selectedRoiNumbers}
                    onClose={() => setUnionOpen(false)}
                    onSuccess={handleUnionSuccess}
                />
            )}
            {copyOpen && patient && sourceImage && (
                <CopyStructureSetDialog
                    open={copyOpen}
                    onClose={handleCloseCopyDialog}
                    onConfirm={handleConfirmCopy}
                    sourceStructureSet={structureSet}
                    patient={patient}
                    sourceImage={sourceImage}
                    initialSelectedRoiNumbers={selectedRoiNumbers}
                />
            )}
        </>
    );
}
